import {useEffect, useState, useCallback} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {Button} from '@origamirisk/origami-storybook'
import {Box, Checkbox, FormControlLabel, Typography, Autocomplete, TextField, LinearProgress} from '@mui/material'
import axios from 'axios'
import {AgGridReact} from 'ag-grid-react'
import {formatEffectiveDate, formatFullTimestamp} from '../utils/dateFormatter'
import {AccountMeta} from '../utils/types'
import {useAccount} from '../context/AccountContext'
import {useImportJobsSSE, VersionSearchUpdate, PermutationResult} from '../hooks/useImportJobsSSE'
import {ImportJobsPanel, JobsButton, ImportJob} from '../components/ImportJobs'

/** Sentinel carrier id for API meaning "any carrier" (matches LambdaVersionManager Constants.NOT_APPLICABLE_ID). */
const BULK_RATING_ANY_CARRIER = '-1'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import './VersionPublisher.css'

interface Version {
    versionId: string
    label?: string
    state: string
    clientName?: string
    policySetName?: string
    carrierName?: string
    effectiveDate: string
    publishTimestamp: string
    versionKeys?: string[]
}


function VersionPublisher() {
    const navigate = useNavigate()
    const location = useLocation()
    const {selectedAccountConnection} = useAccount()
    const selectedAccount = location.state?.selectedAccount || ''
    const [accountMeta, setAccountMeta] = useState<AccountMeta | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>('')
    const [selectedStates, setSelectedStates] = useState<string[]>([])
    const [selectedPolicySet, setSelectedPolicySet] = useState<string | null>(null)
    const [selectedCarriers, setSelectedCarriers] = useState<string[]>([])
    const [loadingVersions, setLoadingVersions] = useState(false)
    const [versionsSearched, setVersionsSearched] = useState(false)
    const [importing, setImporting] = useState(false)
    const [importCommonDefinitions, setImportCommonDefinitions] = useState(true)
    const [importMetadata, setImportMetadata] = useState(true)
    const [importCommonTables, setImportCommonTables] = useState(true)
    const [importGlobalScripts, setImportGlobalScripts] = useState(true)
    const [importRateTables, setImportRateTables] = useState(true)
    const [importAlgo, setImportAlgo] = useState(true)
    const [importValidations, setImportValidations] = useState(true)
    const [applyToExistingVersionsOnly, setApplyToExistingVersionsOnly] = useState(false)
    const [excludeIssuedProposals, setExcludeIssuedProposals] = useState(false)
    const [isImportPanelOpen, setIsImportPanelOpen] = useState(false)
    const [importJobs, setImportJobs] = useState<ImportJob[]>([])
    const [versionSearchRequestId, setVersionSearchRequestId] = useState<string | null>(null)
    const [versionSearchPercent, setVersionSearchPercent] = useState(0)
    const [isSearching, setIsSearching] = useState(false)
    const [searchPermutationResults, setSearchPermutationResults] = useState<PermutationResult[]>([])

    // Get account connection ID from context
    const accountConnectionId = selectedAccountConnection?.id || null

    // Initial load of jobs
    useEffect(() => {
        const fetchJobs = async () => {
            if (!selectedAccount) {
                setImportJobs([])
                return
            }

            try {
                const response = await fetch('/api/import-jobs/list', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({accountName: selectedAccount, limit: 50})
                })

                if (response.ok) {
                    const data = await response.json()
                    setImportJobs(data)
                }
            } catch (error) {
                console.error('Failed to fetch jobs:', error)
            }
        }

        fetchJobs()
    }, [selectedAccount])

    // Handle real-time job updates via SSE
    const handleJobUpdate = useCallback((updatedJob: ImportJob) => {
        setImportJobs(prevJobs => {
            // Remove cancelled or dead-lettered jobs from the list
            if (updatedJob.status === 'CANCELLED' || updatedJob.status === 'DEAD_LETTERED') {
                return prevJobs.filter(j => j.jobId !== updatedJob.jobId)
            }

            const existingIndex = prevJobs.findIndex(j => j.jobId === updatedJob.jobId)

            if (existingIndex >= 0) {
                // Update existing job
                const newJobs = [...prevJobs]
                newJobs[existingIndex] = updatedJob
                return newJobs
            } else {
                // Add new job at the beginning
                return [updatedJob, ...prevJobs]
            }
        })
    }, [])

    const handleSSEError = useCallback((error: Error) => {
        console.error('SSE error:', error)
    }, [])

    const [foundVersions, setFoundVersions] = useState<Version[]>([])

    const handleVersionSearchUpdate = useCallback((update: VersionSearchUpdate) => {
        if (update.requestId !== versionSearchRequestId) return

        setVersionSearchPercent(update.percent)

        if (update.completedPermutation) {
            setSearchPermutationResults(prev => [...prev, update.completedPermutation!])
        }

        if (update.status === 'COMPLETED') {
            // Fetch the results
            axios.get(`/api/find/versions/to/import/${update.requestId}/results`)
                .then(({data}) => {
                    const versions = data.versions || data || []
                    setFoundVersions(versions)
                    setVersionsSearched(true)
                })
                .catch((err) => {
                    console.error('Failed to fetch search results:', err)
                    alert('Failed to fetch search results')
                })
                .finally(() => {
                    setIsSearching(false)
                    setLoadingVersions(false)
                    setVersionSearchRequestId(null)
                    setVersionSearchPercent(0)
                    setSearchPermutationResults([])
                })
        } else if (update.status === 'FAILED') {
            alert(update.errorMessage || 'Version search failed')
            setIsSearching(false)
            setLoadingVersions(false)
            setVersionSearchRequestId(null)
            setVersionSearchPercent(0)
            setSearchPermutationResults([])
        }
    }, [versionSearchRequestId])

    // Use SSE for real-time updates
    useImportJobsSSE({
        accountConnectionId,
        enabled: true, // Always enabled in this view
        onJobUpdate: handleJobUpdate,
        onVersionSearchUpdate: handleVersionSearchUpdate,
        onError: handleSSEError
    })

    const handleOpenImportPanel = () => {
        setIsImportPanelOpen(true)
    }

    // Calculate active jobs count
    const activeJobsCount = importJobs.filter(
        job => job.status === 'QUEUED' || job.status === 'IN_PROGRESS'
    ).length

    const statesForApi = (): string[] => {
        if (!accountMeta) return []
        return selectedStates.map(id => accountMeta.stateMap[id]).filter(Boolean)
    }

    const carrierIdsForApi = (): number[] => {
        if (selectedCarriers.includes(BULK_RATING_ANY_CARRIER)) return [-1]
        return selectedCarriers.map(id => parseInt(id, 10))
    }

    const handleFindVersions = async () => {
        const selectedPolicySet_parsed = selectedPolicySet ? parseInt(selectedPolicySet, 10) : null
        const currentPolicySetMeta = accountMeta?.policySets.find(ps => ps.id === selectedPolicySet)

        // If UseVersionKeys is false, States and Carriers are not required
        const needsStatesAndCarriers = currentPolicySetMeta?.useVersionKeys ?? true

        if (!selectedPolicySet || (needsStatesAndCarriers && (selectedCarriers.length === 0 || selectedStates.length === 0))) {
            alert('Please select at least one option for all fields')
            return
        }

        setLoadingVersions(true)
        setIsSearching(true)
        setVersionSearchPercent(0)
        setFoundVersions([])
        setVersionsSearched(false)
        setSearchPermutationResults([])
        try {
            const payload = {
                policySetId: selectedPolicySet_parsed,
                carrierIds: carrierIdsForApi(),
                states: needsStatesAndCarriers ? statesForApi() : [],
                accountName: selectedAccount,
                importMetadata: true,
                importCommonTables: true,
                importGlobalScripts: true,
                importRateTables: true,
                importAlgo: true,
                importValidations: true,
                applyToExistingVersionsOnly: false,
                excludeIssuedProposals
            }

            const {data} = await axios.post('/api/find/versions/to/import', payload)
            setVersionSearchRequestId(data.requestId)
        } catch (err) {
            console.error('Failed to start version search:', err)
            alert('Failed to start version search')
            setIsSearching(false)
            setLoadingVersions(false)
        }
    }

    const handleImport = async () => {
        const selectedPolicySet_parsed = selectedPolicySet ? parseInt(selectedPolicySet, 10) : null
        const currentPolicySetMeta = accountMeta?.policySets.find(ps => ps.id === selectedPolicySet)

        // If UseVersionKeys is false, States and Carriers are not required
        const needsStatesAndCarriers = currentPolicySetMeta?.useVersionKeys ?? true

        if (!selectedPolicySet || (needsStatesAndCarriers && (selectedCarriers.length === 0 || selectedStates.length === 0))) {
            alert('Please select at least one option for all fields')
            return
        }

        setImporting(true)
        try {
            const payload = {
                policySetId: selectedPolicySet_parsed,
                carrierIds: carrierIdsForApi(),
                states: needsStatesAndCarriers ? statesForApi() : [],
                accountName: selectedAccount,
                importMetadata: applyToExistingVersionsOnly ? importMetadata : true,
                importCommonTables: applyToExistingVersionsOnly ? importCommonTables : true,
                importGlobalScripts: applyToExistingVersionsOnly ? importGlobalScripts : true,
                importRateTables: applyToExistingVersionsOnly ? importRateTables : true,
                importAlgo: applyToExistingVersionsOnly ? importAlgo : true,
                importValidations: applyToExistingVersionsOnly ? importValidations : true,
                applyToExistingVersionsOnly,
                excludeIssuedProposals
            }

            if (!applyToExistingVersionsOnly && foundVersions?.length > 0)
            {
                // set the payload states to just the ones in the foundVersions
                payload.states = [...new Set(foundVersions.map(v => v.state))];
            }

            await axios.post('/api/publish/product/versions', payload)

            // Clear the found versions after successful import
            // Note: Jobs will appear via SSE updates automatically
            setFoundVersions([])
        } catch (err) {
            console.error('Failed to import versions:', err)
            alert('Failed to import versions')
        } finally {
            setImporting(false)
        }
    }

    useEffect(() => {
        const fetchAccountMeta = async () => {
            if (!selectedAccount) return

            setLoading(true)
            setError('')
            try {
                const {data} = await axios.get(`/api/admin/account/meta`)
                setAccountMeta(data)
            } catch (err) {
                console.error('Failed to fetch account metadata:', err)
                setError('Failed to load account metadata')
            } finally {
                setLoading(false)
            }
        }

        fetchAccountMeta()
    }, [selectedAccount])

    return (
        <div>
            <Box sx={{mb: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="h5" sx={{fontWeight: 600}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <ArrowBackIcon style={{color: '#1976d2', cursor: 'pointer'}} sx={{fontSize: 20}}
                                       onClick={() => navigate('/', {state: {selectedAccount}})}/>
                        Version publishing
                    </Box>
                </Typography>
                <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                    <JobsButton
                        activeJobsCount={activeJobsCount}
                        onClick={handleOpenImportPanel}
                        disabled={!selectedAccount}
                    />
                </Box>
            </Box>

            {loading && <p>Loading account metadata...</p>}
            {error && <p style={{color: 'red'}}>{error}</p>}

            {accountMeta && (
                <Box sx={{mt: 4}}>

                    <Box sx={{
                        mt: 3,
                        p: 0,
                        backgroundColor: '#eceff1',
                        borderRadius: '4px',
                        border: '1px solid #cfd8dc'
                    }}>


                        <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4, alignItems: 'flex-start'}}>
                            {/* Rating Program - Single Select */}
                            <Box sx={{flex: '1 1 200px', minWidth: '200px', padding: '10px'}}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1
                                }}>
                                    <Typography variant="body2" sx={{fontWeight: 500}}>Rating Program</Typography>
                                </Box>
                                <Autocomplete
                                    size="small"
                                    options={accountMeta.policySets.map(ps => ps.id).sort((a, b) => {
                                        const nameA = accountMeta.policySets.find(ps => ps.id === a)?.name || '';
                                        const nameB = accountMeta.policySets.find(ps => ps.id === b)?.name || '';
                                        return nameA.localeCompare(nameB);
                                    })}
                                    value={selectedPolicySet}
                                    onChange={(_, newValue) => {
                                        setSelectedPolicySet(newValue)
                                        // Reset dependent fields when policy set changes
                                        setSelectedStates([])
                                        setSelectedCarriers([])
                                    }}
                                    getOptionLabel={(option) => accountMeta.policySets.find(ps => ps.id === option)?.name || option}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select a rating program" />
                                    )}
                                    sx={{
                                        '& .MuiAutocomplete-endAdornment': {
                                            right: '7px !important'
                                        },
                                        '& .MuiInputBase-root': {
                                            paddingRight: '11px !important'
                                        }
                                    }}
                                />
                            </Box>

                            {/* State - Only show if selected PolicySet has UseVersionKeys=true */}
                            <Box sx={{
                                flex: '1 1 200px',
                                minWidth: '200px',
                                padding: '10px',
                                display: selectedPolicySet && accountMeta.policySets.find(ps => ps.id === selectedPolicySet)?.useVersionKeys ? 'block' : 'none'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1
                                }}>
                                    <Typography variant="body2" sx={{fontWeight: 500}}>State</Typography>
                                    <Box sx={{display: 'flex', gap: 1}}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                '&:hover': {textDecoration: 'underline'}
                                            }}
                                            onClick={() => setSelectedStates(Object.keys(accountMeta.stateMap))}
                                        >
                                            All
                                        </Typography>
                                        <Typography variant="caption" sx={{color: '#999'}}>|</Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                '&:hover': {textDecoration: 'underline'}
                                            }}
                                            onClick={() => setSelectedStates([])}
                                        >
                                            None
                                        </Typography>
                                    </Box>
                                </Box>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={Object.keys(accountMeta.stateMap).sort((a, b) =>
                                        accountMeta.stateMap[a].localeCompare(accountMeta.stateMap[b]))}
                                    value={selectedStates}
                                    onChange={(_, newValue) => {
                                        setSelectedStates(newValue)
                                    }}
                                    getOptionLabel={(option) => accountMeta.stateMap[option]}
                                    renderInput={(params) => (
                                        <TextField {...params} />
                                    )}
                                    disableCloseOnSelect
                                    sx={{
                                        '& .MuiAutocomplete-endAdornment': {
                                            right: '7px !important'
                                        },
                                        '& .MuiInputBase-root': {
                                            paddingRight: '11px !important'
                                        }
                                    }}
                                    slotProps={{
                                        chip: {
                                            size: 'small',
                                            className: 'version-publisher-chip'
                                        }
                                    }}
                                />
                            </Box>

                            {/* Carrier - Only show if selected PolicySet has UseVersionKeys=true */}
                            <Box sx={{
                                flex: '1 1 200px',
                                minWidth: '200px',
                                padding: '10px',
                                display: selectedPolicySet && accountMeta.policySets.find(ps => ps.id === selectedPolicySet)?.useVersionKeys ? 'block' : 'none'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1
                                }}>
                                    <Typography variant="body2" sx={{fontWeight: 500}}>Carrier</Typography>
                                    <Box sx={{display: 'flex', gap: 1}}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                '&:hover': {textDecoration: 'underline'}
                                            }}
                                            onClick={() => setSelectedCarriers(Object.keys(accountMeta.carrierMap))}
                                        >
                                            All
                                        </Typography>
                                        <Typography variant="caption" sx={{color: '#999'}}>|</Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                '&:hover': {textDecoration: 'underline'}
                                            }}
                                            onClick={() => setSelectedCarriers([])}
                                        >
                                            None
                                        </Typography>
                                    </Box>
                                </Box>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={Object.keys(accountMeta.carrierMap).sort((a, b) =>
                                        accountMeta.carrierMap[a].localeCompare(accountMeta.carrierMap[b]))}
                                    value={selectedCarriers}
                                    onChange={(_, newValue) => {
                                        setSelectedCarriers(newValue)
                                    }}
                                    getOptionLabel={(option) => accountMeta.carrierMap[option]}
                                    renderInput={(params) => (
                                        <TextField {...params} />
                                    )}
                                    disableCloseOnSelect
                                    sx={{
                                        '& .MuiAutocomplete-endAdornment': {
                                            right: '7px !important'
                                        },
                                        '& .MuiInputBase-root': {
                                            paddingRight: '11px !important'
                                        }
                                    }}
                                    slotProps={{
                                        chip: {
                                            size: 'small',
                                            className: 'version-publisher-chip'
                                        }
                                    }}
                                />
                            </Box>

                        </Box>

                        <div style={{flex: 1, borderBottom: '1px solid #cfd8dc', marginTop: '15px'}}></div>

                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start'}}>

                            {/*Target versions*/}
                            <div className="group-wrapper" style={{padding: '10px'}}>
                                <div className="group-title">Target versions</div>
                                <div className="group-body" style={{paddingRight: '5px'}}>
                                    {(() => {
                                        const currentPolicySetMeta = accountMeta?.policySets.find(ps => ps.id === selectedPolicySet)
                                        const needsStatesAndCarriers = currentPolicySetMeta?.useVersionKeys ?? true
                                        const isDisabled = !selectedPolicySet || (needsStatesAndCarriers && (selectedCarriers.length === 0 || selectedStates.length === 0))
                                        return (
                                            <>
                                                <FormControlLabel
                                                    control={<Checkbox checked={applyToExistingVersionsOnly}
                                                                       onChange={(e) => {
                                                                           setApplyToExistingVersionsOnly(e.target.checked)
                                                                           // Clear found versions and search state when toggling
                                                                           if (e.target.checked) {
                                                                               setFoundVersions([])
                                                                               setVersionsSearched(false)
                                                                           }
                                                                       }}
                                                                       disabled={isDisabled}/>}
                                                    label="Update existing versions only"
                                                />

                                                {!applyToExistingVersionsOnly && (
                                                    <Button
                                                        style={{marginLeft: '5px', fontSize: '13px', fontFamily: 'inherit'}}
                                                        color="secondary"
                                                        variant="contained"
                                                        size="sm"
                                                        onClick={handleFindVersions}
                                                        disabled={isDisabled || loadingVersions || isSearching}
                                                    >
                                                        {isSearching ? 'Searching...' : 'Or, find new versions'}
                                                    </Button>
                                                )}
                                            </>
                                        )
                                    })()}

                                </div>
                            </div>

                            {applyToExistingVersionsOnly && (
                                <>
                                    {(() => {
                                        const currentPolicySetMeta = accountMeta?.policySets.find(ps => ps.id === selectedPolicySet)
                                        const needsStatesAndCarriers = currentPolicySetMeta?.useVersionKeys ?? true
                                        const isDisabled = !selectedPolicySet || (needsStatesAndCarriers && (selectedCarriers.length === 0 || selectedStates.length === 0))
                                        return (
                                            <>
                                                {/*Shared by all versions*/}
                                                <div className="group-wrapper"
                                                     style={{borderLeft: '1px solid #cfd8dc', padding: '10px 10px 10px 17px'}}>
                                                    <div className="group-title">Shared by all versions</div>
                                                    <div className="group-body">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={applyToExistingVersionsOnly ? importCommonDefinitions : true}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked
                                                                    setImportCommonDefinitions(checked)
                                                                    setImportMetadata(checked)
                                                                    setImportCommonTables(checked)
                                                                    setImportGlobalScripts(checked)
                                                                }}
                                                                disabled={!applyToExistingVersionsOnly || isDisabled}/>}
                                                            label="Import common definitions"
                                                        />
                                                    </div>
                                                </div>

                                                {/*Rating Components*/}
                                                <div className="group-wrapper"
                                                     style={{borderLeft: '1px solid #cfd8dc', padding: '10px 0px 10px 17px'}}>
                                                    <div className="group-title">Rating Components / Version Specific</div>
                                                    <div className="group-body">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={applyToExistingVersionsOnly ? importRateTables : true}
                                                                onChange={(e) => setImportRateTables(e.target.checked)}
                                                                disabled={!applyToExistingVersionsOnly || isDisabled}/>}
                                                            label="Rate Tables"
                                                        />
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={applyToExistingVersionsOnly ? importAlgo : true}
                                                                onChange={(e) => setImportAlgo(e.target.checked)}
                                                                disabled={!applyToExistingVersionsOnly || isDisabled}/>}
                                                            label="Rating Algo"
                                                        />
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={applyToExistingVersionsOnly ? importValidations : true}
                                                                onChange={(e) => setImportValidations(e.target.checked)}
                                                                disabled={!applyToExistingVersionsOnly || isDisabled}/>}
                                                            label="Validations"
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    })()}
                                </>
                            )}

                            {/* for now, hide the UI for exclude issued proposals.  It will default to false */}
                            {false && !applyToExistingVersionsOnly && (
                                <>
                                    {/*How to handle existing proposals*/}
                                    <div className="group-wrapper"
                                         style={{borderLeft: '1px solid #cfd8dc', padding: '10px 10px 10px 17px'}}>
                                        <div className="group-title">Existing Proposals</div>
                                        <div className="group-body">
                                            <FormControlLabel
                                                control={<Checkbox
                                                    checked={excludeIssuedProposals}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked
                                                        setExcludeIssuedProposals(!applyToExistingVersionsOnly && checked)
                                                    }}
                                                    disabled={applyToExistingVersionsOnly}/>}
                                                label="Exclude Issued Proposals"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <Box sx={{marginLeft: 'auto', marginTop: '22px', padding: '10px',}}>
                                {(() => {
                                    const currentPolicySetMeta = accountMeta?.policySets.find(ps => ps.id === selectedPolicySet)
                                    const needsStatesAndCarriers = currentPolicySetMeta?.useVersionKeys ?? true
                                    const baseDisabled = !selectedPolicySet || (needsStatesAndCarriers && (selectedCarriers.length === 0 || selectedStates.length === 0)) || importing
                                    const additionalDisabled = (applyToExistingVersionsOnly && !importCommonDefinitions && !importRateTables && !importAlgo && !importValidations) || (!applyToExistingVersionsOnly && foundVersions.length === 0)
                                    return (
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            size="md"
                                            onClick={handleImport}
                                            disabled={baseDisabled || additionalDisabled}
                                        >
                                            {importing ? 'Queueing Jobs...' : 'Queue Publish Job(s)'}
                                        </Button>
                                    )
                                })()}
                            </Box>
                        </Box>
                    </Box>

                    {isSearching && (
                        <Box sx={{mt: 4}}>
                            <Box sx={{
                                p: 3,
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                                    <Typography variant="body2" sx={{fontWeight: 600, color: '#263238'}}>
                                        Searching for versions...
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {versionSearchPercent}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={versionSearchPercent}
                                    sx={{height: 6, borderRadius: 3, mb: 2}}
                                />
                                {searchPermutationResults.length > 0 && (
                                    <Box sx={{
                                        maxHeight: 300,
                                        overflowY: 'auto',
                                        mt: 1
                                    }}>
                                        {searchPermutationResults.map((p, i) => {
                                            const state = p.state || ''
                                            const client = p.clientName || accountMeta?.clientMap[String(p.clientId)] || String(p.clientId)
                                            const policySet = p.policySetName || accountMeta?.policySets.find(ps => ps.id === String(p.policySetId))?.name || String(p.policySetId)
                                            const carrier = p.carrierName || accountMeta?.carrierMap[String(p.carrierId)] || String(p.carrierId)
                                            return (
                                                <Typography key={i} variant="caption" sx={{
                                                    display: 'block',
                                                    color: p.versionsFound > 0 ? '#2e7d32' : '#999',
                                                    lineHeight: 1.8
                                                }}>
                                                    {state}, {client}, {policySet}, {carrier}: {p.versionsFound > 0
                                                        ? <span style={{fontWeight: 500}}>found {p.versionsFound} version{p.versionsFound !== 1 ? 's' : ''}</span>
                                                        : 'no new versions'}
                                                </Typography>
                                            )
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}

                    {foundVersions.length > 0 && (
                        <Box sx={{mt: 4}}>
                            <Typography variant="body2" sx={{mb: 2, fontWeight: 600, color: '#263238'}}>
                                Versions to publish ({foundVersions.length})
                            </Typography>
                            <Box sx={{height: 'calc(100vh - 500px)', minHeight: 400, width: '100%'}}>
                                <div className="ag-theme-balham" style={{height: '100%', width: '100%'}}>
                                    <AgGridReact
                                        rowData={foundVersions}
                                        rowHeight={34}
                                        enableCellTextSelection={true}
                                        ensureDomOrder={true}
                                        autoSizeStrategy={{type: 'fitGridWidth', defaultMinWidth: 100}}
                                        columnDefs={[
                                            {
                                                field: 'versionId',
                                                headerName: 'Version',
                                                flex: 2,
                                                minWidth: 100,
                                                maxWidth: 120,
                                                valueGetter: (params) => params.data?.label || params.data?.versionId || '',
                                                comparator: (_valueA: string, _valueB: string, nodeA: any, nodeB: any) => {
                                                    const displayA = nodeA.data?.label || nodeA.data?.versionId
                                                    const displayB = nodeB.data?.label || nodeB.data?.versionId

                                                    if (!displayA || !displayB) {
                                                        return displayA ? 1 : displayB ? -1 : 0
                                                    }

                                                    const partsA = displayA.split('.').map(Number)
                                                    const partsB = displayB.split('.').map(Number)
                                                    const maxLength = Math.max(partsA.length, partsB.length)

                                                    for (let i = 0; i < maxLength; i++) {
                                                        const a = partsA[i] || 0
                                                        const b = partsB[i] || 0
                                                        if (a !== b) {
                                                            return a - b
                                                        }
                                                    }

                                                    return 0
                                                }
                                            },
                                            {
                                                field: 'state',
                                                headerName: 'State',
                                                flex: 0.5,
                                                minWidth: 80,
                                                maxWidth: 100
                                            },
                                            {
                                                field: 'clientName',
                                                headerName: 'Client',
                                                flex: 1.5,
                                                minWidth: 150
                                            },
                                            {
                                                field: 'policySetName',
                                                headerName: 'Policy',
                                                flex: 1.5,
                                                minWidth: 150
                                            },
                                            {
                                                field: 'carrierName',
                                                headerName: 'Carrier',
                                                flex: 0.8,
                                                minWidth: 100
                                            },
                                            {
                                                field: 'effectiveDate',
                                                headerName: 'Effective Date',
                                                flex: 1,
                                                minWidth: 120,
                                                valueFormatter: (params: any) => formatEffectiveDate(params.data?.effectiveDate),
                                                filter: 'agDateColumnFilter'
                                            },
                                            {
                                                field: 'publishTimestamp',
                                                headerName: 'Published',
                                                flex: 1.2,
                                                minWidth: 170,
                                                valueFormatter: (params: any) => formatFullTimestamp(params.data?.publishTimestamp),
                                                filter: 'agDateColumnFilter'
                                            }
                                        ]}
                                        defaultColDef={{
                                            sortable: true,
                                            filter: true,
                                            resizable: true
                                        }}
                                        pagination={true}
                                    />
                                </div>
                            </Box>
                        </Box>
                    )}

                    {versionsSearched && foundVersions.length === 0 && !loadingVersions && !isSearching && (
                        <Box sx={{
                            mt: 4,
                            p: 4,
                            textAlign: 'center',
                            color: '#666',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0'
                        }}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                No new versions found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                There are no new versions available to import with the selected criteria.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            <ImportJobsPanel
                open={isImportPanelOpen}
                onClose={() => setIsImportPanelOpen(false)}
                policySetMap={accountMeta?.policySets.reduce((acc, ps) => {acc[ps.id] = ps.name; return acc}, {} as Record<string, string>)}
            />
        </div>
    )
}

export default VersionPublisher
